import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  RiAddLine,
  RiAlarmWarningLine,
  RiBellLine,
  RiCalculatorLine,
  RiCalendarCheckLine,
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiExternalLinkLine,
  RiGovernmentLine,
  RiShieldLine,
  RiArrowRightLine,
} from 'react-icons/ri';
import { FiZap } from 'react-icons/fi';

import HeaderFirst from 'utils/HeaderFirst';
import TableLoader from 'components/Loader/TableLoader';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import { useDossierRole } from 'contexts/DossierRoleContext';
import { useGetObligationsFiscalesQuery } from 'actions/fiscalite/queries';
import {
  useDeclarerObligationFiscaleMutation,
  useDeleteObligationFiscaleMutation,
  useSeedObligationsFiscalesMutation,
} from 'actions/fiscalite/mutations';
import { toastApiErrors } from 'utils/toastApiError';

import ObligationCard from './ObligationCard';
import ModalPaiementFiscal from './ModalPaiementFiscal';
import ModalObligationForm from './ModalObligationForm';
import SimulateurFiscal from './SimulateurFiscal';
import ParametresFiscaux from './ParametresFiscaux';
import './fiscalite.css';

const LIST_TABS = [
  { key: 'all', label: 'Toutes les échéances' },
  { key: 'urgents', label: 'Urgents / En retard', cls: 'fisc-tab-urgent', icon: <RiAlarmWarningLine /> },
  { key: 'pending', label: 'À payer', cls: 'fisc-tab-pending' },
  { key: 'paid', label: 'Payés', cls: 'fisc-tab-paid' },
];

const TOOL_TABS = [
  { key: 'simulator', label: 'Simulateur TVA & Impôts', cls: 'fisc-tab-dark', icon: <RiCalculatorLine /> },
  { key: 'settings', label: 'Rappels SMS / Email', cls: 'fisc-tab-dark', icon: <RiBellLine /> },
];

const estUrgent = (o) =>
  o.statut_effectif === 'EN_RETARD' ||
  (o.statut_effectif !== 'PAYE' &&
    o.jours_restants !== null &&
    o.jours_restants !== undefined &&
    o.jours_restants <= 15);

const filtres = {
  all: () => true,
  urgents: estUrgent,
  pending: (o) => ['EN_ATTENTE', 'DECLARE'].includes(o.statut_effectif),
  paid: (o) => o.statut_effectif === 'PAYE',
};

const formatMontant = (m) => Number(m || 0).toLocaleString('fr-FR');

const formatDateLongue = (iso) => {
  if (!iso) return ':';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const FiscalitePage = () => {
  const { clientID: dossierId } = useParams();
  const { canEdit } = useDossierRole();

  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedForPayment, setSelectedForPayment] = useState(null);
  const [formModal, setFormModal] = useState({ open: false, obligation: null });
  const [toDelete, setToDelete] = useState(null);

  const { data, isLoading, refetch } = useGetObligationsFiscalesQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );
  const [declarerObligation] = useDeclarerObligationFiscaleMutation();
  const [deleteObligation, { isLoading: isDeleting }] =
    useDeleteObligationFiscaleMutation();
  const [seedObligations, { isLoading: isSeeding }] =
    useSeedObligationsFiscalesMutation();

  const obligations = useMemo(() => {
    const list = data?.data || [];
    return [...list].sort(
      (a, b) => new Date(a.date_echeance) - new Date(b.date_echeance)
    );
  }, [data]);

  const enRetard = useMemo(
    () => obligations.filter((o) => o.statut_effectif === 'EN_RETARD'),
    [obligations]
  );

  const kpis = useMemo(() => {
    const impayees = obligations.filter((o) => o.statut_effectif !== 'PAYE');
    const totalARégler = impayees.reduce((s, o) => s + Number(o.montant || 0), 0);
    const totalPaye = obligations
      .filter((o) => o.statut_effectif === 'PAYE')
      .reduce((s, o) => s + Number(o.montant || 0), 0);
    const prochaine = impayees.length ? impayees[0].date_echeance : null;
    const now = new Date();
    const duMois = impayees.filter((o) => {
      if (!o.date_echeance) return false;
      const d = new Date(o.date_echeance);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return { totalARégler, totalPaye, prochaine, duMois };
  }, [obligations]);

  const compteurs = useMemo(
    () => ({
      all: obligations.length,
      urgents: obligations.filter(estUrgent).length,
      pending: obligations.filter(filtres.pending).length,
      paid: obligations.filter(filtres.paid).length,
    }),
    [obligations]
  );

  const visibles = useMemo(() => {
    const filtre = filtres[activeTab] || filtres.all;
    const term = search.trim().toLowerCase();
    return obligations.filter(filtre).filter((o) => {
      if (!term) return true;
      return [o.nom, o.code, o.periode, o.organisme_label]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [obligations, activeTab, search]);

  const handleSeed = async () => {
    try {
      const res = await seedObligations({ dossier_id: dossierId }).unwrap();
      toast.success(res.message || 'Calendrier fiscal initialisé.');
    } catch (error) {
      toastApiErrors(error);
    }
  };

  const handleDeclarer = async (obligation) => {
    try {
      await declarerObligation({ id: obligation.id, dossier_id: dossierId }).unwrap();
      toast.success(`« ${obligation.nom} » marquée comme déclarée.`);
    } catch (error) {
      toastApiErrors(error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteObligation(toDelete.id).unwrap();
      toast.success('Échéance fiscale supprimée.');
      setToDelete(null);
      refetch();
    } catch (error) {
      toastApiErrors(error);
      setToDelete(null);
    }
  };

  const showList = ['all', 'urgents', 'pending', 'paid'].includes(activeTab);

  const nom = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <h4 className="titre-dossier">
        <RiGovernmentLine style={{ marginRight: 8, color: 'var(--sidebar-green, #009688)' }} />
        Impôts &amp; Fiscalité
      </h4>
      <span className="fisc-badge fisc-badge-success">DGID / TeleDAC</span>
      <span className="nbrDossier">{obligations.length}</span>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '15px',
      }}
    >
      <div className="d-flex align-items-center flex-wrap pb-3">
        <HeaderFirst
          nom={nom}
          description={
            <div>
              Rappels automatiques pour le versement des impôts avant les dates
              limites (le 15 de chaque mois).{' '}
              <a
                href="https://etax.dgid.sn"
                target="_blank"
                rel="noopener noreferrer"
                className="fisc-kpi-link"
              >
                Portail e-Tax DGID <RiExternalLinkLine />
              </a>
            </div>
          }
        />
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={{ borderRadius: '31px', padding: '8px 16px', fontSize: 14 }}
              onClick={handleSeed}
              disabled={isSeeding}
              title="Créer les échéances standards : TVA, VRS, IPRES/CSS, BRS, CEL, acomptes IS"
            >
              <RiCalendarCheckLine style={{ marginRight: 6 }} />
              {isSeeding ? 'Initialisation...' : 'Calendrier fiscal SN'}
            </button>
            <button
              type="button"
              className="btn"
              style={{
                borderRadius: '31px',
                padding: '8px 16px',
                backgroundColor: 'var(--sidebar-green, #009688)',
                color: 'white',
                fontSize: 14,
                fontWeight: 500,
              }}
              onClick={() => setFormModal({ open: true, obligation: null })}
            >
              <RiAddLine style={{ marginRight: 4 }} /> Ajouter une échéance
            </button>
          </div>
        )}
      </div>

      {/* Bandeau alerte retards */}
      {enRetard.length > 0 && (
        <div className="fisc-alert-retard">
          <div className="fisc-alert-retard-main">
            <div className="fisc-alert-retard-icon">
              <RiShieldLine size={24} />
            </div>
            <div>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <h3>{enRetard.length} IMPÔT(S) EN RETARD DE PAIEMENT !</h3>
                <span className="fisc-alert-retard-tag">RISQUE DE PÉNALITÉ</span>
              </div>
              <p>
                Date butoir dépassée pour :{' '}
                <strong>{enRetard.map((o) => o.nom).join(', ')}</strong>.
                Régularisez le versement rapidement pour limiter le taux de
                majoration.
              </p>
            </div>
          </div>
          {canEdit && (
            <button
              type="button"
              className="fisc-alert-retard-btn"
              onClick={() => setSelectedForPayment(enRetard[0])}
            >
              Régler l&apos;impôt en retard <RiArrowRightLine />
            </button>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="fisc-kpis">
        <div className="fisc-kpi">
          <div className="fisc-kpi-top">
            <span>Total impôts à régler</span>
            <div className="fisc-kpi-icon fisc-kpi-icon-amber"><RiBellLine /></div>
          </div>
          <div className="fisc-kpi-value">{formatMontant(kpis.totalARégler)} FCFA</div>
          <div className="fisc-kpi-sub fisc-kpi-sub-amber">
            {compteurs.pending} échéance(s) en attente
          </div>
        </div>

        <div className="fisc-kpi">
          <div className="fisc-kpi-top">
            <span>Prochaine date butoir</span>
            <div className="fisc-kpi-icon fisc-kpi-icon-green"><RiCalendarLine /></div>
          </div>
          <div className="fisc-kpi-value fisc-kpi-value-green">
            {formatDateLongue(kpis.prochaine)}
          </div>
          <div className="fisc-kpi-sub">
            Règle générale DGID : <strong>le 15 de chaque mois</strong>
          </div>
        </div>

        <div className="fisc-kpi">
          <div className="fisc-kpi-top">
            <span>Total déjà acquitté</span>
            <div className="fisc-kpi-icon fisc-kpi-icon-blue"><RiCheckboxCircleLine /></div>
          </div>
          <div className="fisc-kpi-value">{formatMontant(kpis.totalPaye)} FCFA</div>
          <div className="fisc-kpi-sub fisc-kpi-sub-green">Quitus fiscal à jour</div>
        </div>

        <div className="fisc-kpi">
          <div className="fisc-kpi-top">
            <span>Échéances ce mois-ci</span>
            <div className="fisc-kpi-icon fisc-kpi-icon-purple"><FiZap /></div>
          </div>
          <div className="fisc-kpi-value fisc-kpi-value-purple">
            {kpis.duMois.length} échéance{kpis.duMois.length > 1 ? 's' : ''}
          </div>
          <div className="fisc-kpi-sub">
            {formatMontant(kpis.duMois.reduce((s, o) => s + Number(o.montant || 0), 0))} FCFA
            à régler en {new Date().toLocaleDateString('fr-FR', { month: 'long' })}
          </div>
        </div>
      </div>

      {/* Onglets filtres + outils */}
      <div className="fisc-tabs">
        {LIST_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`fisc-tab ${tab.cls || ''} ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
            {compteurs[tab.key] !== undefined && (
              <span className="fisc-tab-count">{compteurs[tab.key]}</span>
            )}
          </button>
        ))}
        <span className="fisc-tabs-sep" aria-hidden="true" />
        {TOOL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`fisc-tab ${tab.cls || ''} ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Barre de recherche sur les onglets liste */}
      {showList && (
        <div className="form-group" style={{ maxWidth: 420 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher (nom, code, organisme, période)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Listes */}
      {showList && (
        isLoading ? (
          <TableLoader
            message="Chargement des échéances fiscales..."
            subMessage="Récupération des obligations du dossier"
            minHeight="240px"
          />
        ) : visibles.length === 0 ? (
          <div className="fisc-empty">
            <RiGovernmentLine size={40} />
            <p className="mt-2 mb-1">Aucune échéance fiscale dans cet onglet.</p>
            {canEdit && obligations.length === 0 && (
              <p className="text-muted" style={{ fontSize: 13 }}>
                Utilisez « Calendrier fiscal SN » pour initialiser les échéances
                standards (TVA, VRS, IPRES/CSS, BRS, CEL, acomptes IS).
              </p>
            )}
          </div>
        ) : (
          <div className="fisc-list">
            <div className="fisc-list-head">
              <span>
                <RiCalendarLine style={{ marginRight: 6, color: 'var(--sidebar-green, #009688)' }} />
                Liste des échéances fiscales &amp; sociales (Sénégal)
              </span>
            </div>
            {visibles.map((obligation) => (
              <ObligationCard
                key={obligation.id}
                obligation={obligation}
                canEdit={canEdit}
                onPayer={setSelectedForPayment}
                onDeclarer={handleDeclarer}
                onEdit={(o) => setFormModal({ open: true, obligation: o })}
                onDelete={setToDelete}
              />
            ))}
          </div>
        )
      )}

      {/* Simulateur */}
      {activeTab === 'simulator' && <SimulateurFiscal />}

      {/* Paramètres */}
      {activeTab === 'settings' && (
        <ParametresFiscaux dossierId={dossierId} canEdit={canEdit} />
      )}

      {/* Modals */}
      <ModalPaiementFiscal
        isOpen={Boolean(selectedForPayment)}
        onClose={() => setSelectedForPayment(null)}
        obligation={selectedForPayment}
        dossierId={dossierId}
      />
      <ModalObligationForm
        isOpen={formModal.open}
        onClose={() => setFormModal({ open: false, obligation: null })}
        dossierId={dossierId}
        obligation={formModal.obligation}
      />
      <ConfirmDeleteModal
        show={Boolean(toDelete)}
        onHide={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'échéance fiscale"
        message={`Supprimer « ${toDelete?.nom} » ?`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FiscalitePage;
